import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";

export default class extends Controller {
    static targets = ["buttonContainer", "button", "dropdown", "logo"]

    initialize() {
        this.colors = this.getColors();

        this.width = this.element.clientWidth + 15;
        this.init();

        window.addEventListener("resize", () => {
            this.deleteChart();
            this.width = this.element.clientWidth + 15;
            this.init();
        });
    }

    init() {
        this.setAttributes();
        this.setButtonWidth();
        this.logoTarget.style.transform = `scale(${this.width * 0.08 / 100})`;
        this.buttonContainerTarget.style.height = this.height + "px";
        this.createChart();
        this.setButtonTranslate();
    }

    setAttributes() {
        this.height = Math.min(350, this.width / 2);
        this.pieData = Array(this.buttonTargets.length).fill(1);
        this.outerRadius = this.height / 2 - 85;
        this.lineAngle = 60;
        this.buttonCoordinates = [];
        this.curve = d3.line().curve(d3.curveLinear);
    }

    getColors() {
        const colors = [];
        this.buttonTargets.forEach(buttonElement => {
            colors.push(buttonElement.dataset.color);
        });
        return colors;
    }

    setButtonWidth() {
        let maxButtonWidth = 0;
        this.buttonTargets.forEach(buttonElement => {
            buttonElement.style.width = null;
            maxButtonWidth = maxButtonWidth > buttonElement.clientWidth ? maxButtonWidth : buttonElement.clientWidth;
        });

        this.buttonTargets.forEach(buttonElement => {
            buttonElement.style.width = maxButtonWidth + 1 + "px";
        });

        this.dropdownTargets.forEach(dropdownElement => {
            dropdownElement.style.width = maxButtonWidth + 1 + "px";
        });
        this.maxButtonWidth = maxButtonWidth;
    }

    setButtonTranslate() {
        for (let i = 0; i < this.buttonCoordinates.length; i++) {
            const buttonCoordinate = this.buttonCoordinates[i];
            const buttonElement = this.buttonTargets[i];
            let translateX = buttonCoordinate.x + this.width / 2;
            translateX -= buttonCoordinate.x < 0 ? this.maxButtonWidth - 62 : -45;
            if (translateX < 10) {
                translateX = 10;
            } else if (translateX > this.width - this.maxButtonWidth - 30 / 2 - 10) {
                translateX = this.width - this.maxButtonWidth - 30 / 2 - 10;
            }
            buttonElement.style.transform = `translate(${translateX}px, ${buttonCoordinate.y + this.height / 2 - 21}px)`;
        };
    }

    createChart() {
        const staleSVG = this.element.querySelector("svg[class='chart']");
        if (staleSVG) {
            staleSVG.remove();
        };

        this.svg = d3.create("svg")
            .attr("viewBox", [-this.width / 2, -this.height / 2, this.width, this.height])
            .attr("class", "chart");
        const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(this.outerRadius);

        const pie = d3.pie().sort(null).value((d) => d);

        const g = this.svg.selectAll(".arc")
            .data(pie(this.pieData))
            .enter()
            .append("g")
            .attr("class", "arc");

        const labelr = this.outerRadius + 60;
        g.append("point")
            .attr("x", function (d) {
                let a = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2;
                d.cx = Math.cos(a) * (labelr - 75);
                d.x = Math.cos(a) * (labelr - 20) * 2;
                return d.x;
            })
            .attr("y", function (d) {
                let a = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2;
                d.cy = Math.sin(a) * (labelr - 75);
                d.y = Math.sin(a) * (labelr - 20);
                return d.y;
            })
            .attr("transform", "translate(0, 5)")
            .each(function (d) {
                d.sx = d.x - this.lineAngle;
                d.ox = d.x + this.lineAngle;
                d.sy = d.oy = d.y;
                this.buttonCoordinates.push({
                    x: d.sx,
                    y: d.sy
                })
            }.bind(this))

        g.append("path")
            .style("fill", "none")
            .attr("class", (d, i, n) => "stroke-" + this.colors[i])
            .style("stroke-width", 1.7)
            .attr("d", this.getLine.bind(this));

        g.append('path')
            .attr("d", arc)
            .attr("class", "arc")
            .attr("fill", "#F1F2F7");

        const logoScale = parseFloat(this.logoTarget.style.transform.replace("scale(", "").replace(")", ""))
        const logoBoundingBox = this.logoTarget.getBoundingClientRect();
        const rectWidth = logoBoundingBox.width * logoScale * 0.25;
        this.svg.append('rect')
            .attr("fill", "white")
            .attr("transform", `translate(${this.outerRadius}, -5)`)
            .attr("width", rectWidth)
            .attr("height", 20);

        this.svg.append('rect')
            .attr("fill", "white")
            .attr("transform", `translate(${-this.outerRadius - rectWidth}, -5)`)
            .attr("width", rectWidth)
            .attr("height", 20);

        this.element.appendChild(Object.assign(this.svg.node()));
    }

    deleteChart() {
        this.svg.remove();
    }

    getLine(d) {
        let points;
        if (d.cx > d.ox) {
            points = [[d.sx, d.sy], [d.ox, d.oy], [d.cx, d.cy]];
        } else {
            points = [[d.ox, d.oy], [d.sx, d.sy], [d.cx, d.cy]];
        }
        return this.curve(points);
    }
}